import { useCallback, useState } from 'react';
import uuid from 'react-uuid';
import { api } from 'src/constants/apiRoutes';
import useAsync from 'src/hooks/useAsync';
import { calculatePercentage } from 'src/utils';
import axiosInstance from 'src/utils/axios';
import { convertDate } from 'src/utils/transformation';
import { BuyerInfo, SupplierInfo, Trigger } from './useInvoiceDetailsApi';
import { PoDocumentObject } from './usePoDocumentsApi';

export interface PeriodAllocationItem {
  glPeriodId: number;
  amount: number;
  percentageAmount: number;
  isClosed: number;
}

export const PoTrigger = [
  'Startup',
  'SubmitForApproval',
  'Recall',
  'Cancel',
  'BuyerAccept',
  'SupplierAccept',
  'CreateReceipt',
  'CreateInvoice',
  'Close',
  'Decline',
  'Update',
] as const;

interface PoDetailsResponseObject {
  glPeriodId: number;
  poId: number;
  poNum: string;
  numberOfReceipts: number;
  poStatus: string;
  statusDate: string;
  creationDate: string;
  supplierName: string;
  buyerName: string;
  poSubject: string;
  poQty: number;
  receiptTTLQty: number;
  buyerId: string;
  companyId: number;
  numberOfInvoices: number;
  poTotal: number;
  isEDIReceipt: boolean;
  invoiceTotal: number;
  invoiceLineItemQty: number;
  triggerPreCheck: Trigger[];
  isOffline: boolean;
  documents: PoDocumentObject[];
  allocations?: PeriodAllocationItem[];
  supplier?: SupplierInfo;
  buyer?: BuyerInfo;
}

export type PoDetailsResponse = PoDetailsResponseObject;

export interface PoDetailsObject extends Omit<PoDetailsResponseObject, 'statusDate'> {
  statusDate: Date;
}

export type PoDetails = PoDetailsObject;

const usePoDetailsApi = (poId: string) => {
  const [poDocuments, setPoDocuments] = useState<PoDocumentObject[]>([]);

  const call = useCallback(
    () =>
      new Promise<PoDetails>((resolve, reject) => {
        if (poId) {
          axiosInstance
            .get<PoDetailsResponse>(api.poDetails(poId))
            .then((data) => {
              resolve({
                ...data.data,
                statusDate: convertDate(data?.data?.statusDate),
                allocations:
                  data?.data?.allocations?.map((allocation) => ({
                    ...allocation,
                    percentageAmount: calculatePercentage(data.data.poTotal, allocation.amount),
                  })) || [],
              });
              setPoDocuments(
                data?.data?.documents.map((item) => ({
                  ...item,
                  documentTotal: item?.documentType === 'Receipt' ? -1 : item?.documentTotal,
                  statusDate: convertDate(item.statusDate),
                  rowId: uuid(),
                  selected: false,
                  poId,
                }))
              );
            })
            .catch((error) => {
              reject(error);
            });
        }
      }),
    [poId]
  );

  const { isLoading, value, setValue, execute } = useAsync(call, true);

  return {
    isLoading,
    poDetails: value,
    setPoDetails: setValue,
    refresh: execute,
    poDocuments,
    setPoDocuments,
  };
};

export default usePoDetailsApi;
